import { useEffect } from "react";
import { useAppDispatch } from "app/store";
import { useGetUIContentQuery } from "features/ui-content/endpoints";
import {
  uiContentLoaded,
  noticeUiContentLoaded,
  litigationUiContentLoaded,
} from "features/ui-content/slice";
import { useTeamCapabilities } from "hooks/useTeamCapabilities";
import { GaiaConstants } from "utils";

/**
 * Hook to load UI content when current resource team changes
 * @param currentResourceTeam The current resource team name
 */
export const useUIContentLoader = (currentResourceTeam: string) => {
  const dispatch = useAppDispatch();

  const { hasAuditCapability, hasNoticeCapability, hasLitigationCapability } =
    useTeamCapabilities();

  // Fetch UI content based on capabilities
  const { data: auditUiContentData, isLoading: isLoadingAuditUiContent } =
    useGetUIContentQuery(
      { teamName: currentResourceTeam || "", page: "createaudit" },
      {
        skip:
          !currentResourceTeam ||
          !hasAuditCapability ||
          currentResourceTeam === GaiaConstants.PLACE_HOLDER,
      },
    );

  const { data: noticeUiContentData, isLoading: isLoadingNoticeUiContent } =
    useGetUIContentQuery(
      { teamName: currentResourceTeam || "", page: "notice" },
      {
        skip:
          !currentResourceTeam ||
          !hasNoticeCapability ||
          currentResourceTeam === GaiaConstants.PLACE_HOLDER,
      },
    );

  const {
    data: litigationUiContentData,
    isLoading: isLoadingLitigationUiContent,
  } = useGetUIContentQuery(
    { teamName: currentResourceTeam || "", page: "litigation" },
    {
      skip:
        !currentResourceTeam ||
        !hasLitigationCapability ||
        currentResourceTeam === GaiaConstants.PLACE_HOLDER,
    },
  );

  // Dispatch data to store when received
  useEffect(() => {
    if (auditUiContentData) {
      dispatch(uiContentLoaded(auditUiContentData));
    }
  }, [dispatch, auditUiContentData, currentResourceTeam]);

  useEffect(() => {
    if (noticeUiContentData) {
      dispatch(noticeUiContentLoaded(noticeUiContentData));
    }
  }, [dispatch, noticeUiContentData, currentResourceTeam]);

  useEffect(() => {
    if (litigationUiContentData) {
      dispatch(litigationUiContentLoaded(litigationUiContentData));
    }
  }, [dispatch, litigationUiContentData, currentResourceTeam]);

  return {
    isLoadingAuditUiContent,
    isLoadingNoticeUiContent,
    isLoadingLitigationUiContent,
    hasAuditCapability,
    hasNoticeCapability,
    hasLitigationCapability,
  };
};
